import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import EditIcon from "@mui/icons-material/EditOutlined";
import "../../../styles/admin/promptPopup.scss";

const CreateEvalPopup = ({
  open,
  onClose,
  onSave,
  evalType,
  initialPrompt,
  readOnly = false,
  onEditClick,
}) => {
  const displayType = initialPrompt?.types || evalType || "content_eval";
  const showJsonSchema = [
    "content_eval",
    "ai_tutor_eval",
    "personalized_quiz_eval",
    "quiz_eval",
    "feedback",
    "ai_tutor","test_result_feedback_eval","personalized_content_eval","test_chat_bot","think_analysis_validation",
    "adaptive_learning_eval"
  ].includes(displayType);
  const showPersona = displayType === "ai_tutor" || displayType === "think_analysis_validation" || displayType === "test_chat_bot";

  const [name, setName] = useState("");
  const [promptText, setPromptText] = useState("");
  const [jsonSchemaGoogle, setJsonSchemaGoogle] = useState("");
  const [persona, setPersona] = useState("");
  const [validationError, setValidationError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!name.trim() || !promptText.trim()) return;
    if (validationError) return;

    const payload = {
      name: name.trim(),
      prompt: promptText.trim(),
      types: displayType,
    };

    if (showJsonSchema) {
      payload.json_mode = jsonSchemaGoogle.trim() !== "" || (initialPrompt ? !!initialPrompt.json_mode : [
        "content_eval",
        "ai_tutor_eval",
        "personalized_quiz_eval",
        "quiz_eval",
        "feedback","ai_tutor"
      ].includes(displayType));
      payload.json_schema = jsonSchemaGoogle.trim();
      payload.json_schema_google = jsonSchemaGoogle.trim();
    } else {
      payload.json_mode = false;
      payload.json_schema = "";
      payload.json_schema_google = "";
    }

    if (showPersona) {
      payload.persona = persona.trim();
    } else {
      payload.persona = "";
    }

    try {
      setSaving(true);
      if (onSave) {
        await onSave(payload);
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (initialPrompt) {
        setName(initialPrompt.name || "");
        setPromptText(initialPrompt.prompt || "");
        
        let schemaVal = initialPrompt.json_schema_google || initialPrompt.json_schema || "";
        if (schemaVal && typeof schemaVal === "string") {
          try {
            const parsed = JSON.parse(schemaVal);
            schemaVal = JSON.stringify(parsed, null, 2);
          } catch (e) {
            // Keep original string if parsing fails
          }
        } else if (schemaVal && typeof schemaVal === "object") {
          schemaVal = JSON.stringify(schemaVal, null, 2);
        }
        
        setJsonSchemaGoogle(schemaVal);
        setPersona(initialPrompt.persona || "");
      } else {
        setName("");
        setPromptText("");
        setJsonSchemaGoogle("");
        setPersona("");
      }
      setSaving(false);
      setValidationError("");
    }
  }, [open, initialPrompt]);

  useEffect(() => {
    if (!showJsonSchema || !jsonSchemaGoogle.trim()) {
      setValidationError("");
      return;
    }
    try {
      JSON.parse(jsonSchemaGoogle);
      setValidationError("");
    } catch (err) {
      setValidationError("Invalid JSON: " + err.message);
    }
  }, [jsonSchemaGoogle, showJsonSchema]);

  if (!open) return null;

  const formattedType = displayType === "ai_tutor"
    ? "AI Tutor"
    : displayType === "ai_tutor_eval"
    ? "AI Tutor Evaluation"
    : displayType.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return createPortal(
    <div
      className="common-popup-overlay high-z-index persona-create-popup"
    >
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header with-close">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2>{readOnly ? "View System Prompt" : initialPrompt ? "Edit System Prompt" : "Create System Prompt"}</h2>
            <span
              style={{
                backgroundColor: 'rgba(109, 74, 255, 0.08)',
                color: '#6d4aff',
                padding: '4px 10px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 600,
                border: '1px solid rgba(109, 74, 255, 0.15)',
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              {formattedType}
            </span>
          </div>
          <button
            type="button"
            className="popup-close-button"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div className="popup-content" style={{ flex: 1, overflowY: 'auto' }}>
            <div className="popup-form-group">
              <label>Name</label>
              <input
                type="text"
                className="popup-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                readOnly={readOnly}
                placeholder="Prompt name"
                required
              />
            </div>

            {showPersona && (
              <div className="popup-form-group">
                <label>Persona</label>
                <textarea
                  className="popup-textarea"
                  rows={4}
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  readOnly={readOnly}
                  placeholder="Enter the persona instructions"
                  style={{ minHeight: 100, color: "var(--color-text-light)" }}
                  required
                />
              </div>
            )}

            <div className="popup-form-group">
              <label>System Prompt</label>
              <textarea
                className="popup-textarea"
                rows={8}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                readOnly={readOnly}
                placeholder="Enter system prompt instructions"
                style={{ minHeight: 180, color: "var(--color-text-light)" }}
                required
              />
            </div>

            {showJsonSchema && (
              <div className="popup-form-group">
                <label>JSON Schema (Google)</label>
                <textarea
                  className="popup-textarea"
                  rows={8}
                  value={jsonSchemaGoogle}
                  onChange={(e) => setJsonSchemaGoogle(e.target.value)}
                  readOnly={readOnly}
                  placeholder="Enter JSON schema for Google Gemini"
                  style={{
                    minHeight: 180,
                    fontFamily: 'monospace',
                    borderColor: validationError ? '#d32f2f' : undefined
                  }}
                />
                {validationError && (
                  <span style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {validationError}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="popup-actions" style={{ padding: '16px 20px', borderTop: '1px solid var(--secondary-border)', background: 'var(--color-bg)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            {readOnly ? (
              <>
                <button
                  type="button"
                  className="popup-cancel-button"
                  onClick={onClose}
                >
                  Close
                </button>
                {onEditClick && (
                  <button
                    type="button"
                    className="popup-submit-button"
                    onClick={onEditClick}
                  >
                    <EditIcon fontSize="small" style={{ marginRight: '6px' }} />
                    Edit System Prompt
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="popup-cancel-button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="popup-submit-button"
                  disabled={saving || !name.trim() || !promptText.trim() || !!validationError}
                >
                  {saving ? "Saving..." : initialPrompt ? "Save Changes" : "Create Prompt"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateEvalPopup;
